import {
  estimatePercentileFromTomkinsonCentiles,
  getTomkinsonCentilesForAgeBand,
  TOMKINSON_HANDGRIP_2025_ABSOLUTE_KG,
  TOMKINSON_HANDGRIP_2025_CITATION,
  TOMKINSON_HANDGRIP_2025_NORMALIZED_KG_PER_M2,
  TOMKINSON_HANDGRIP_2025_PERCENTILES,
  type TomkinsonHandgripSex,
} from './handgripTomkinson2025';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

export type HandgripSide = 'right' | 'left';

export type HandgripChartImages = {
  absolute?: string;
  normalized?: string;
};

export type HandgripNormSummary = {
  percentile: number | null;
  referenceP5: number | null;
  predictedP50: number | null;
  percentPredicted: number | null;
};

export type HandgripResultPayload = {
  unit: string;
  dominantHand: HandgripSide;
  right: {
    attempts: number[];
    best: number | null;
    mean: number | null;
  };
  left: {
    attempts: number[];
    best: number | null;
    mean: number | null;
  };
  bestOverall: number | null;
  normalizedOverall: number | null;
  norms: {
    source: 'Tomkinson2025';
    citation: string;
    sex: TomkinsonHandgripSex;
    ageYears: number | null;
    heightCm: number | null;
    absoluteKg: HandgripNormSummary;
    normalizedKgPerM2: HandgripNormSummary;
  };
  notes?: string;
  finishedAt?: string;
  patientContext?: {
    birthDate?: string | null;
    ageYears?: number | null;
    sex?: string | null;
    weightKg?: number | null;
    heightCm?: number | null;
    identifier?: string | null;
  } | null;
  professionalContext?: {
    name?: string | null;
    registration?: string | null;
    role?: string | null;
  } | null;
};

export type HandgripPatientContext = {
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
};

type HandgripBuildInput = {
  dominantHand: HandgripSide;
  rightAttempts: number[];
  leftAttempts: number[];
  notes?: string;
  patientContext?: HandgripPatientContext | null;
  finishedAt?: string;
};

const normalizeAttemptSeries = (values: unknown): number[] =>
  Array.isArray(values)
    ? values
        .map((value) => toFiniteNumber(value))
        .filter((value): value is number => value != null && value > 0)
    : [];

const round = (value: number | null | undefined, decimals = 1): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Number(value.toFixed(decimals));
};

const computeAttemptStats = (attempts: number[]) => {
  const best = attempts.length ? Math.max(...attempts) : null;
  const mean = attempts.length ? attempts.reduce((sum, value) => sum + value, 0) / attempts.length : null;
  return {
    attempts,
    best: round(best, 1),
    mean: round(mean, 1),
  };
};

export const resolveHandgripSex = (sex?: string | null): TomkinsonHandgripSex =>
  String(sex || '').toUpperCase() === 'F' ? 'F' : 'M';

export const getHandgripDominantHandLabel = (side?: HandgripSide | null): string =>
  side === 'left' ? 'Esquerda' : 'Direita';

export const getHandgripPlotAge = (ageYears?: number | null): number | null => {
  if (typeof ageYears !== 'number' || !Number.isFinite(ageYears)) return null;
  return Math.max(20, Math.min(100, ageYears));
};

export const getHandgripChartData = (
  sex: TomkinsonHandgripSex,
  mode: 'absolute' | 'normalized',
): Array<Record<string, number>> => {
  const rows = mode === 'absolute' ? TOMKINSON_HANDGRIP_2025_ABSOLUTE_KG[sex] : TOMKINSON_HANDGRIP_2025_NORMALIZED_KG_PER_M2[sex];
  return rows.map((row) => {
    const item: Record<string, number> = { age: row.age, ageStart: row.ageStart, ageEnd: row.ageEnd };
    TOMKINSON_HANDGRIP_2025_PERCENTILES.forEach((percentile) => {
      item[`P${percentile}`] = row.values[percentile];
    });
    return item;
  });
};

export const formatHandgripChartAgeBand = (row: unknown, fallbackAge: unknown): string => {
  const source = row && typeof row === 'object' ? (row as Record<string, unknown>) : null;
  const ageStart = toFiniteNumber(source?.ageStart);
  const ageEnd = toFiniteNumber(source?.ageEnd);

  if (ageStart === 100) return 'Faixa etária: 100+ anos';
  if (ageStart != null && ageEnd != null) return `Faixa etária: ${ageStart}-${ageEnd} anos`;

  const age = toFiniteNumber(fallbackAge);
  return age != null ? `Idade: ${age} anos` : 'Faixa etária';
};

export const HANDGRIP_AGE_TICKS = [20, 30, 40, 50, 60, 70, 80, 90, 100] as const;

export const getHandgripChartAxisConfig = (
  rows: Array<Record<string, number>>,
  patientValue: number | null,
  mode: 'absolute' | 'normalized',
) => {
  const step = mode === 'absolute' ? 10 : 5;
  const minimumMaximum = mode === 'absolute' ? 50 : 20;
  const referenceValues = rows.flatMap((row) =>
    TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => row[`P${percentile}`]).filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value),
    ),
  );
  const values = patientValue != null && Number.isFinite(patientValue) ? [...referenceValues, patientValue] : referenceValues;
  const observedMaximum = values.length ? Math.max(...values) : minimumMaximum;
  const roundedMaximum = Math.ceil(observedMaximum / step) * step;
  const maximum = Math.max(minimumMaximum, roundedMaximum <= observedMaximum ? roundedMaximum + step : roundedMaximum);

  return {
    xDomain: [20, 100] as [number, number],
    xTicks: [...HANDGRIP_AGE_TICKS],
    yDomain: [0, maximum] as [number, number],
    yTicks: Array.from({ length: maximum / step + 1 }, (_, index) => index * step),
    yStep: step,
    xLabel: 'Idade (anos)',
    yLabel: mode === 'absolute' ? 'Força (kg)' : 'Força/altura² (kg/m²)',
  };
};

export const computeHandgripNorms = (
  bestOverall: number | null,
  patientContext?: HandgripPatientContext | null,
): HandgripResultPayload['norms'] => {
  const sex = resolveHandgripSex(patientContext?.sex);
  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const heightCm = normalizePatientHeightCm(patientContext?.heightCm);
  const heightM = heightCm != null && heightCm > 0 ? heightCm / 100 : null;
  const normalizedOverall =
    bestOverall != null && heightM != null && heightM > 0 ? bestOverall / (heightM * heightM) : null;

  const absoluteCentiles = ageYears != null ? getTomkinsonCentilesForAgeBand(TOMKINSON_HANDGRIP_2025_ABSOLUTE_KG[sex], ageYears) : null;
  const normalizedCentiles =
    ageYears != null ? getTomkinsonCentilesForAgeBand(TOMKINSON_HANDGRIP_2025_NORMALIZED_KG_PER_M2[sex], ageYears) : null;

  const absMedian = absoluteCentiles?.[50] ?? null;
  const normMedian = normalizedCentiles?.[50] ?? null;
  const absoluteP5 = absoluteCentiles?.[5] ?? null;
  const normalizedP5 = normalizedCentiles?.[5] ?? null;
  const absolutePercentile =
    absoluteCentiles && bestOverall != null ? estimatePercentileFromTomkinsonCentiles(absoluteCentiles, bestOverall) : null;
  const normalizedPercentile =
    normalizedCentiles && normalizedOverall != null
      ? estimatePercentileFromTomkinsonCentiles(normalizedCentiles, normalizedOverall)
      : null;

  return {
    source: 'Tomkinson2025',
    citation: TOMKINSON_HANDGRIP_2025_CITATION,
    sex,
    ageYears,
    heightCm,
    absoluteKg: {
      percentile: round(absolutePercentile, 1),
      referenceP5: round(absoluteP5, 1),
      predictedP50: round(absMedian, 1),
      percentPredicted: absMedian != null && bestOverall != null && absMedian > 0 ? round((bestOverall / absMedian) * 100, 0) : null,
    },
    normalizedKgPerM2: {
      percentile: round(normalizedPercentile, 1),
      referenceP5: round(normalizedP5, 1),
      predictedP50: round(normMedian, 1),
      percentPredicted:
        normMedian != null && normalizedOverall != null && normMedian > 0 ? round((normalizedOverall / normMedian) * 100, 0) : null,
    },
  };
};

export const buildHandgripPayload = ({
  dominantHand,
  rightAttempts,
  leftAttempts,
  notes = '',
  patientContext,
  finishedAt,
}: HandgripBuildInput): HandgripResultPayload => {
  const normalizedRightAttempts = normalizeAttemptSeries(rightAttempts);
  const normalizedLeftAttempts = normalizeAttemptSeries(leftAttempts);
  const right = computeAttemptStats(normalizedRightAttempts);
  const left = computeAttemptStats(normalizedLeftAttempts);
  const bestOverall = round(
    [right.best, left.best].filter((value): value is number => value != null).length
      ? Math.max(...[right.best, left.best].filter((value): value is number => value != null))
      : null,
    1,
  );
  const heightCm = normalizePatientHeightCm(patientContext?.heightCm);
  const heightM = heightCm != null && heightCm > 0 ? heightCm / 100 : null;
  const normalizedOverall = bestOverall != null && heightM != null && heightM > 0 ? round(bestOverall / (heightM * heightM), 2) : null;
  const norms = computeHandgripNorms(bestOverall, patientContext);

  return {
    unit: 'kg',
    dominantHand,
    right,
    left,
    bestOverall,
    normalizedOverall,
    norms,
    notes,
    finishedAt: finishedAt || new Date().toISOString(),
    patientContext: patientContext
      ? {
          ageYears: toFiniteNumber(patientContext.ageYears),
          sex: patientContext.sex ?? null,
          weightKg: normalizePatientWeightKg(patientContext.weightKg),
          heightCm: normalizePatientHeightCm(patientContext.heightCm),
        }
      : null,
  };
};

export const normalizeHandgripPayload = (payload: any): HandgripResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;

  const rightAttempts = normalizeAttemptSeries(payload.right?.attempts);
  const leftAttempts = normalizeAttemptSeries(payload.left?.attempts);
  const dominantHand: HandgripSide = payload.dominantHand === 'left' ? 'left' : 'right';
  const inferredPatientContext = {
    ageYears: toFiniteNumber(payload.patientContext?.ageYears ?? payload.norms?.ageYears),
    sex: payload.patientContext?.sex ?? payload.norms?.sex ?? null,
    weightKg: normalizePatientWeightKg(payload.patientContext?.weightKg),
    heightCm: normalizePatientHeightCm(payload.patientContext?.heightCm ?? payload.norms?.heightCm),
  };

  const normalized = buildHandgripPayload({
    dominantHand,
    rightAttempts,
    leftAttempts,
    notes: typeof payload.notes === 'string' ? payload.notes : '',
    patientContext: inferredPatientContext,
    finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
  });

  return {
    ...normalized,
    unit: typeof payload.unit === 'string' ? payload.unit : normalized.unit,
    notes: typeof payload.notes === 'string' ? payload.notes : normalized.notes,
    finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : normalized.finishedAt,
    patientContext: payload.patientContext && typeof payload.patientContext === 'object' ? payload.patientContext : undefined,
    professionalContext:
      payload.professionalContext && typeof payload.professionalContext === 'object' ? payload.professionalContext : undefined,
  };
};

export const summarizeHandgripResult = (payload: HandgripResultPayload | null | undefined) => {
  const normalized = normalizeHandgripPayload(payload);
  const ageYears = normalized?.norms?.ageYears ?? null;
  const sex = normalized?.norms?.sex ?? null;
  const heightCm = normalized?.norms?.heightCm ?? null;
  const absolutePercentile = normalized?.norms?.absoluteKg?.percentile ?? null;
  const absoluteReferenceP5 = normalized?.norms?.absoluteKg?.referenceP5 ?? null;
  const absolutePercentPredicted = normalized?.norms?.absoluteKg?.percentPredicted ?? null;
  const normalizedPercentile = normalized?.norms?.normalizedKgPerM2?.percentile ?? null;
  const normalizedReferenceP5 = normalized?.norms?.normalizedKgPerM2?.referenceP5 ?? null;
  const normalizedPercentPredicted = normalized?.norms?.normalizedKgPerM2?.percentPredicted ?? null;
  const bestOverallSide: HandgripSide | null = normalized?.bestOverall == null
    ? null
    : normalized.right.best === normalized.left.best
      ? normalized.dominantHand
      : normalized.right.best != null && (normalized.left.best == null || normalized.right.best > normalized.left.best)
        ? 'right'
        : 'left';

  return {
    unit: normalized?.unit ?? 'kg',
    dominantHand: normalized?.dominantHand ?? 'right',
    dominantHandLabel: getHandgripDominantHandLabel(normalized?.dominantHand),
    bestRight: normalized?.right.best ?? null,
    bestLeft: normalized?.left.best ?? null,
    meanRight: normalized?.right.mean ?? null,
    meanLeft: normalized?.left.mean ?? null,
    bestOverall: normalized?.bestOverall ?? null,
    bestOverallSide,
    bestOverallSideLabel: bestOverallSide ? getHandgripDominantHandLabel(bestOverallSide) : '—',
    normalizedOverall: normalized?.normalizedOverall ?? null,
    absolutePercentile,
    absoluteReferenceP5,
    absolutePercentPredicted,
    normalizedPercentile,
    normalizedReferenceP5,
    normalizedPercentPredicted,
    citation: normalized?.norms?.citation ?? TOMKINSON_HANDGRIP_2025_CITATION,
    ageYears,
    heightCm,
    sex,
    plotAge: getHandgripPlotAge(ageYears),
    notes: normalized?.notes ?? '',
    rightAttempts: normalized?.right.attempts ?? [],
    leftAttempts: normalized?.left.attempts ?? [],
  };
};

export const formatHandgripPercentile = (percentile: number | null | undefined): string => {
  if (typeof percentile !== 'number' || !Number.isFinite(percentile)) return '—';
  if (percentile < 5) return '< P5';
  if (percentile > 95) return '> P95';

  const rounded = Number(percentile.toFixed(1));
  return `P${String(rounded).replace('.', ',')}`;
};
